import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { company, profile, workingAreas } from '../data';

function Portrait({ portraitRef, captionRef }) {
  return (
    <figure
      ref={portraitRef}
      className="hero-portrait"
    >
      <div className="hero-portrait__slab" aria-hidden="true" />
      <div className="hero-portrait__image">
        <picture>
          <source srcSet={profile.images.hero.srcSet} sizes={profile.images.hero.sizes} type="image/webp" />
          <img src={profile.images.hero.fallback} alt={profile.images.hero.alt} width="720" height="960" loading="eager" fetchpriority="high" decoding="async" />
        </picture>
      </div>
      <figcaption ref={captionRef} className="hero-portrait__caption">
        <span className="hero-portrait__caption-symbol" aria-hidden="true">↗</span>
        <span>Good products are<br />a form of direction.</span>
      </figcaption>
      <span className="hero-portrait__serial" aria-hidden="true">PSHR / 26</span>
      <span className="hero-portrait__circle" aria-hidden="true">BUILT<br />WITH<br />INTENT</span>
    </figure>
  );
}

function HeroPortrait({ reduceMotion }) {
  const portraitRef = useRef(null);
  const captionRef = useRef(null);

  useEffect(() => {
    if (reduceMotion) return undefined;

    let timer = 0;
    let frame = 0;
    const enableParallax = () => {
      // Let the first paint and the entrance motion settle before creating
      // scroll observers. The parallax behavior remains unchanged afterwards,
      // without making the portrait's first render wait for an animation runtime.
      timer = window.setTimeout(() => {
        const update = () => {
          frame = 0;
          const progress = Math.min(1, Math.max(0, window.scrollY / 760));
          if (portraitRef.current) portraitRef.current.style.transform = `translate3d(0, ${progress * 58}px, 0)`;
          if (captionRef.current) captionRef.current.style.transform = `translate3d(0, ${progress * -24}px, 0)`;
        };
        const onScroll = () => {
          if (!frame) frame = window.requestAnimationFrame(update);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        cleanup = () => {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        };
      }, 1200);
    };
    let cleanup = () => {};

    if (document.readyState === 'complete') {
      enableParallax();
    } else {
      window.addEventListener('load', enableParallax, { once: true });
    }

    return () => {
      window.removeEventListener('load', enableParallax);
      if (timer) window.clearTimeout(timer);
      if (frame) window.cancelAnimationFrame(frame);
      cleanup();
    };
  }, [reduceMotion]);

  return <Portrait portraitRef={portraitRef} captionRef={captionRef} />;
}

export default function HeroSection() {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  const enterStyle = (index) => ({ '--hero-enter-delay': `${0.12 + index * 0.09}s` });

  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero__paper-grain" aria-hidden="true" />
      <div className="hero__margin-note" aria-hidden="true">
        <span>Field note / 001</span>
        <span>Andhra Pradesh → everywhere</span>
      </div>

      <div className="hero__wrap page-wrap">
        <div className="hero__copy">
          <p className="hero__eyebrow hero-enter" style={enterStyle(0)}>
            <span className="hero__eyebrow-mark" aria-hidden="true">✳</span>
            Independent product practice · 2026
          </p>

          <h1 id="hero-title" className="hero-enter" style={enterStyle(1)}>
            <span>Putha</span>
            <span>Sri Hari</span>
            <em>Reddy</em>
          </h1>

          <div className="hero__thesis hero-enter" style={enterStyle(2)}>
            <span className="hero__thesis-rule" aria-hidden="true" />
            <p>I shape digital products that make a complicated next step feel obvious.</p>
          </div>

          <p className="hero__lede hero-enter" style={enterStyle(3)}>
            Founder and product engineer behind{' '}
            <a href={profile.companyUrl} target="_blank" rel="noopener noreferrer" data-cursor-label="VISIT">{company.name}</a>.
            {' '}{profile.intro}
          </p>

          <div className="hero__actions hero-enter" style={enterStyle(4)}>
            <Link className="button button--ink" to="/work" data-cursor-label="WORK">
              <span className="button__number">01</span>
              <span className="button__label" data-label="Open selected work">Open selected work</span>
              <span aria-hidden="true" className="button__arrow">↗</span>
            </Link>
            <a className="hero__resume-link" href={profile.resume} target="_blank" rel="noopener noreferrer" data-cursor-label="CV">
              <span>Resume / profile</span><i aria-hidden="true">↗</i>
            </a>
          </div>

          <ul className="hero__areas hero-enter" style={enterStyle(5)} aria-label="Areas of work">
            {workingAreas.map((area, index) => <li key={area}><span>0{index + 1}</span>{area}</li>)}
          </ul>
        </div>

        <HeroPortrait reduceMotion={reduceMotion} />
      </div>

      <div className="hero__running-line hero__running-line--enter" aria-label="Portfolio focus">
        <div className="hero__running-track">
          <span>PRODUCT DIRECTION</span><i>✳</i><span>INTERFACE SYSTEMS</span><i>✳</i><span>FULL-STACK DELIVERY</span><i>✳</i><span>AI WORKFLOWS</span><i>✳</i>
          <span>PRODUCT DIRECTION</span><i>✳</i><span>INTERFACE SYSTEMS</span><i>✳</i><span>FULL-STACK DELIVERY</span><i>✳</i><span>AI WORKFLOWS</span><i>✳</i>
        </div>
      </div>
    </section>
  );
}
